<div class="space-y-6 pb-10">
  <div class="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
    <div>
      <h2 class="text-2xl font-bold tracking-tight">Rider Payout Requests</h2>
      <p class="text-base-content/60">
        Review, approve, and manually settle rider withdrawal requests.
      </p>
    </div>
    <button class="btn btn-primary" (click)="loadPayouts()">
      <i class="fa-solid fa-rotate"></i>
      Refresh
    </button>
  </div>

  <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
    <div class="card border border-base-200 bg-base-100 shadow-sm">
      <div class="card-body py-4">
        <span class="text-xs font-semibold uppercase tracking-wider text-base-content/50">Total Requested</span>
        <span class="text-2xl font-bold">{{ totals().requested | indianCompact:true }}</span>
      </div>
    </div>
    <div class="card border border-base-200 bg-base-100 shadow-sm">
      <div class="card-body py-4">
        <span class="text-xs font-semibold uppercase tracking-wider text-base-content/50">Pending</span>
        <span class="text-2xl font-bold text-warning">{{ totals().pending | indianCompact:true }}</span>
      </div>
    </div>
    <div class="card border border-base-200 bg-base-100 shadow-sm">
      <div class="card-body py-4">
        <span class="text-xs font-semibold uppercase tracking-wider text-base-content/50">Approved</span>
        <span class="text-2xl font-bold text-info">{{ totals().approved | indianCompact:true }}</span>
      </div>
    </div>
    <div class="card border border-base-200 bg-base-100 shadow-sm">
      <div class="card-body py-4">
        <span class="text-xs font-semibold uppercase tracking-wider text-base-content/50">Paid</span>
        <span class="text-2xl font-bold text-success">{{ totals().paid | indianCompact:true }}</span>
      </div>
    </div>
    <div class="card border border-base-200 bg-base-100 shadow-sm">
      <div class="card-body py-4">
        <span class="text-xs font-semibold uppercase tracking-wider text-base-content/50">Rejected</span>
        <span class="text-2xl font-bold text-error">{{ totals().rejected | indianCompact:true }}</span>
      </div>
    </div>
  </div>

  <div class="flex flex-wrap items-center gap-3">
    <div class="tabs tabs-boxed w-fit bg-base-200/50 p-1">
      @for (tab of statusTabs; track tab) {
        <button class="tab capitalize" [class.tab-active]="activeFilter === tab" (click)="setFilter(tab)">
          {{ tab }}
        </button>
      }
    </div>

    <select class="select select-bordered select-sm" [(ngModel)]="selectedStore" (ngModelChange)="page = 1; loadPayouts()" [disabled]="!auth.isSuperAdmin()">
      @if(auth.isSuperAdmin()){<option [ngValue]="0">All stores</option>}
      @for (store of stores(); track store.id) {
        <option [ngValue]="store.id">{{ store.name }}</option>
      }
    </select>

    <select class="select select-bordered select-sm min-w-[180px]" [(ngModel)]="selectedRiderId" (ngModelChange)="page = 1; loadPayouts()">
      <option value="">All riders</option>
      @for (rider of riders(); track rider.id) {
        <option [value]="rider.id">{{ rider.full_name || rider.code }}</option>
      }
    </select>

    <input type="date" class="input input-bordered input-sm" [(ngModel)]="fromDate" (ngModelChange)="page = 1; loadPayouts()">
    <input type="date" class="input input-bordered input-sm" [(ngModel)]="toDate" (ngModelChange)="page = 1; loadPayouts()">
  </div>

  <div class="card overflow-hidden border border-base-200 bg-base-100 shadow-sm">
    <div class="overflow-x-auto">
      <table class="table table-md w-full">
        <thead class="bg-base-200/50">
          <tr>
            <th>Request ID</th>
            <th>Rider</th>
            <th>Mobile</th>
            <th>Amount</th>
            <th>Wallet Balance</th>
            <th>Request Date</th>
            <th>Status</th>
            <th>Remarks</th>
            <th class="text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          @for (payout of payouts(); track payout.id) {
            <tr class="hover:bg-base-200/30">
              <td class="font-mono text-sm">#{{ payout.id }}</td>
              <td>
                <div class="font-bold">{{ payout.rider?.full_name || 'Rider' }}</div>
                <div class="text-xs opacity-50">{{ payout.rider?.code || payout.rider_id }}</div>
              </td>
              <td class="font-mono text-sm">{{ payout.rider?.phone || '—' }}</td>
              <td class="font-semibold text-primary">₹{{ payout.amount | number:'1.0-0' }}</td>
              <td>₹{{ (payout.wallet_balance || 0) | number:'1.0-0' }}</td>
              <td>{{ payout.created_at | date:'medium' }}</td>
              <td>
                <span class="badge badge-sm capitalize" [ngClass]="statusClass(payout.status)">
                  {{ payout.status }}
                </span>
              </td>
              <td class="max-w-[180px] truncate text-xs opacity-70" [title]="payout.remarks || ''">
                {{ payout.remarks || payout.transaction_reference || '—' }}
              </td>
              <td class="text-right">
                <div class="flex justify-end gap-2">
                  @if (payout.status === 'pending') {
                    <button class="btn btn-xs btn-primary" (click)="approvePayout(payout)" [disabled]="isSubmitting()">Approve</button>
                    <button class="btn btn-xs btn-outline btn-error" (click)="openReject(payout)">Reject</button>
                  }
                  @if (payout.status === 'approved') {
                    <button class="btn btn-xs btn-success text-white" (click)="openMarkPaid(payout)">Mark Paid</button>
                    <button class="btn btn-xs btn-outline btn-error" (click)="openReject(payout)">Reject</button>
                  }
                </div>
              </td>
            </tr>
          } @empty {
            <tr>
              <td colspan="9" class="py-12 text-center text-base-content/60">No payout requests found.</td>
            </tr>
          }
        </tbody>
      </table>
    </div>

    <div class="flex items-center justify-between border-t border-base-200 bg-base-200/30 p-4">
      <div class="text-sm text-base-content/70">
        Showing {{ totalItems === 0 ? 0 : (page - 1) * pageSize + 1 }} to
        {{ Math.min(page * pageSize, totalItems) }} of {{ totalItems }}
      </div>
      <div class="join">
        <button class="join-item btn btn-sm" [disabled]="page === 1" (click)="onPageChange(page - 1)">«</button>
        <button class="join-item btn btn-sm pointer-events-none">Page {{ page }} / {{ totalPages || 1 }}</button>
        <button class="join-item btn btn-sm" [disabled]="page >= totalPages" (click)="onPageChange(page + 1)">»</button>
      </div>
    </div>
  </div>
</div>

@if (actionPayout(); as payout) {
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
    <div class="w-full max-w-lg rounded-2xl border border-base-300 bg-base-100 p-6 shadow-2xl">
      <h3 class="text-lg font-bold">
        @if (actionType() === 'reject') { Reject Payout #{{ payout.id }} }
        @else { Mark Payout #{{ payout.id }} as Paid }
      </h3>
      <p class="mt-1 text-sm opacity-60">
        Rider: {{ payout.rider?.full_name }} · Amount: ₹{{ payout.amount | number:'1.0-0' }}
      </p>

      @if (actionType() === 'paid') {
        <label for="payout-transaction-reference" class="form-control mt-4 w-full">
          <div class="label"><span class="label-text font-semibold">Transaction reference (UTR / UPI)</span></div>
          <input id="payout-transaction-reference" class="input input-bordered w-full" [(ngModel)]="actionTransactionRef" placeholder="UPI123456789">
        </label>
      }

      <label for="payout-action-remarks" class="form-control mt-4 w-full">
        <div class="label">
          <span class="label-text font-semibold">
            @if (actionType() === 'reject') { Rejection reason }
            @else { Notes (optional) }
          </span>
        </div>
        <textarea id="payout-action-remarks" class="textarea textarea-bordered w-full" rows="3" [(ngModel)]="actionRemarks"></textarea>
      </label>

      <div class="mt-6 flex justify-end gap-2">
        <button class="btn btn-ghost" (click)="closeActionModal()" [disabled]="isSubmitting()">Cancel</button>
        <button
          class="btn"
          [class.btn-error]="actionType() === 'reject'"
          [class.btn-success]="actionType() === 'paid'"
          (click)="submitAction()"
          [disabled]="isSubmitting()"
        >
          @if (actionType() === 'reject') { Reject }
          @else { Confirm Payment }
        </button>
      </div>
    </div>
  </div>
}
